/**
 * Client HTTP ABBUSINESS
 *
 * Fonctionnalités :
 *   - credentials:'include' sur toutes les requêtes (cookie httpOnly ab_token)
 *   - Intercepteur CSRF : lit le cookie XSRF-TOKEN, injecte X-XSRF-TOKEN
 *     sur toutes les mutations (POST, PUT, PATCH, DELETE)
 *   - Timeout 15 s via AbortController
 *   - Retry 3× avec backoff exponentiel (1 s, 2 s, 4 s) sur erreurs réseau
 *     uniquement — jamais sur les erreurs 4xx/5xx
 *   - File d'attente pendant le refresh de token (évite le double refresh)
 *   - 401 → dispatch de l'événement 'auth:unauthorized' sur window
 *   - Erreurs normalisées { message, status, data, isNetworkError }
 *
 * Aucun console.log en production.
 */

import { API_BASE } from './apiBase';

// ── Configuration ─────────────────────────────────────────────────────────────

/** Timeout de chaque requête en millisecondes. */
const REQUEST_TIMEOUT_MS = 15_000;

/** Nombre maximum de tentatives sur erreur réseau. */
const MAX_RETRIES = 3;

/** Délai de base du backoff exponentiel en millisecondes. */
const BACKOFF_BASE_MS = 1_000;

/** Méthodes HTTP considérées comme des mutations (requièrent un token CSRF). */
const MUTATION_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

// ── Helpers localStorage (compat token Bearer jusqu'à migration cookie) ────────

/** Token Sanctum encore stocké en localStorage par les pages Login/Register. */
function getToken() {
  return localStorage.getItem('abbusiness_token');
}

/*
 | Jeton d'appareil de confiance.
 |
 | Remis par le serveur apres un second facteur reussi, renvoye a la
 | connexion suivante : ce navigateur n'a alors plus besoin de code pendant
 | trente jours. Il remplace la regle « pas de code si la derniere connexion
 | date de moins de 72 heures », qui regardait l'horloge du compte et
 | dispensait donc aussi n'importe quel autre appareil tenant le mot de passe.
 |
 | Stocke a cote du jeton de session, et pas ailleurs : celui-ci vit deja en
 | localStorage. Quiconque peut lire l'un peut lire l'autre, et le jeton de
 | session vaut bien davantage — le mettre ici n'ouvre aucune porte qui ne
 | soit deja ouverte. Le jour ou la session passera en cookie httpOnly,
 | celui-ci suivra.
 */
function getDeviceToken() {
  try { return localStorage.getItem('abbusiness_device_token'); }
  catch { return null; }
}

function setDeviceToken(value) {
  try {
    if (value) localStorage.setItem('abbusiness_device_token', value);
  } catch { /* navigation privee, quota : la connexion suivante redemandera un code */ }
}

function clearDeviceToken() {
  try { localStorage.removeItem('abbusiness_device_token'); } catch { /* idem */ }
}

/** Identifiant de session unique par appareil (anti-brute-force). */
function getDeviceId() {
  let id = localStorage.getItem('abbusiness_device_id');
  if (!id) {
    id = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem('abbusiness_device_id', id);
  }
  return id;
}

// ── Log conditionnel ──────────────────────────────────────────────────────────

const isDev = import.meta.env.DEV === true;

/** Log uniquement en développement — aucune trace en production. */
const devLog = isDev
  ? (...args) => console.log('[api]', ...args)
  : () => {};

const devWarn = isDev
  ? (...args) => console.warn('[api]', ...args)
  : () => {};

// ── Gestion du refresh token ──────────────────────────────────────────────────

/**
 * Indique si un refresh est en cours.
 * Évite les appels concurrents qui déclencheraient plusieurs refreshs.
 */
let isRefreshing = false;

/**
 * File d'attente des résolveurs en attente du refresh.
 * @type {Array<{resolve: Function, reject: Function}>}
 */
let refreshQueue = [];

/**
 * Notifie tous les appels en file d'attente après un refresh.
 *
 * @param {Error|null} error - null si le refresh a réussi, Error sinon
 */
function flushRefreshQueue(error) {
  refreshQueue.forEach(({ resolve, reject }) => {
    error ? reject(error) : resolve();
  });
  refreshQueue = [];
}

// ── Utilitaires CSRF ──────────────────────────────────────────────────────────

/**
 * Lit la valeur d'un cookie par son nom.
 * Utilisé pour extraire le XSRF-TOKEN déposé par Laravel Sanctum.
 *
 * @param {string} name - Nom du cookie
 * @returns {string|null}
 */
function getCookie(name) {
  const match = document.cookie.match(
    new RegExp('(?:^|;\\s*)' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)')
  );
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Injecte l'en-tête X-XSRF-TOKEN pour les mutations.
 * Laravel vérifie cet en-tête en complément du cookie de session.
 *
 * @param {string} method  - Méthode HTTP
 * @param {Headers} headers - Objet Headers à muter
 */
function injectCsrfHeader(method, headers) {
  if (MUTATION_METHODS.has(method.toUpperCase())) {
    const xsrf = getCookie('XSRF-TOKEN');
    if (xsrf) {
      headers.set('X-XSRF-TOKEN', xsrf);
    } else {
      devWarn('XSRF-TOKEN cookie absent — la requête peut être rejetée par Laravel.');
    }
  }
}

// ── Normalisation des erreurs ────────────────────────────────────────────────

/**
 * @typedef {Object} ApiError
 * @property {string}  message        - Message lisible
 * @property {number|null} status     - Code HTTP (null si erreur réseau)
 * @property {any}     data           - Corps de réponse (si disponible)
 * @property {boolean} isNetworkError - true si l'erreur est réseau (pas HTTP)
 */

/**
 * Construit une erreur API normalisée.
 *
 * @param {Object} params
 * @returns {ApiError & Error}
 */
function createApiError({ message, status = null, data = null, isNetworkError = false }) {
  const error = new Error(message);
  error.status = status;
  error.data = data;
  error.isNetworkError = isNetworkError;
  return error;
}

// ── Requête principale ───────────────────────────────────────────────────────

/**
 * Effectue une requête fetch avec timeout, CSRF et retry.
 *
 * @param {string} url      - URL relative (ex : '/products') ou absolue
 * @param {RequestInit & { _retryCount?: number }} options
 * @returns {Promise<any>}  - Corps JSON analysé
 * @throws {ApiError & Error}
 */
async function request(url, options = {}) {
  const { _retryCount = 0, ...fetchOptions } = options;

  const fullUrl    = url.startsWith('http') ? url : `${API_BASE}${url}`;
  const method     = (fetchOptions.method ?? 'GET').toUpperCase();
  const controller = new AbortController();
  const timer      = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  // Construction des en-têtes
  const headers = new Headers(fetchOptions.headers ?? {});
  headers.set('Accept', 'application/json');
  if (fetchOptions.body && !(fetchOptions.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  // Compat localStorage : injecte le Bearer tant que le cookie httpOnly
  // n'est pas encore posé par le serveur (transition non cassante).
  const token = getToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  injectCsrfHeader(method, headers);

  devLog(`${method} ${fullUrl}${_retryCount > 0 ? ` (tentative ${_retryCount + 1})` : ''}`);

  let response;
  try {
    response = await fetch(fullUrl, {
      ...fetchOptions,
      method,
      headers,
      credentials: 'include',   // envoie ab_token cookie + XSRF-TOKEN cookie
      signal:      controller.signal,
    });
  } catch (networkError) {
    clearTimeout(timer);

    const isAbortError = networkError.name === 'AbortError';
    const message      = isAbortError
      ? `La requête a expiré après ${REQUEST_TIMEOUT_MS / 1000} s.`
      : 'Erreur réseau — vérifiez votre connexion.';

    // Retry uniquement sur erreurs réseau (pas sur timeout intentionnel)
    if (! isAbortError && _retryCount < MAX_RETRIES) {
      const delay = BACKOFF_BASE_MS * Math.pow(2, _retryCount);
      devWarn(`Erreur réseau — nouvelle tentative dans ${delay} ms…`);
      await sleep(delay);
      return request(url, { ...options, _retryCount: _retryCount + 1 });
    }

    throw createApiError({ message, status: null, isNetworkError: true });
  } finally {
    clearTimeout(timer);
  }

  // ── Gestion 401 (non authentifié) ───────────────────────────────────────
  if (response.status === 401) {
    // N'émettre auth:unauthorized que si on avait un token — sinon c'est
    // un 401 "normal" (ex: restoreSession au démarrage sans cookie/token).
    if (getToken()) {
      devWarn('401 reçu avec token actif — dispatch auth:unauthorized');
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    } else {
      devWarn('401 reçu sans token — session vide, pas de dispatch');
    }
    throw createApiError({
      message: 'Session expirée. Veuillez vous reconnecter.',
      status:  401,
    });
  }

  // ── Réponse sans corps (204) ─────────────────────────────────────────────
  if (response.status === 204) {
    return null;
  }

  // ── Lecture du corps JSON ────────────────────────────────────────────────
  let data;
  try {
    data = await response.json();
  } catch {
    data = null;
  }

  if (! response.ok) {
    const message = data?.message
      ?? data?.error
      ?? `Erreur ${response.status}`;

    throw createApiError({
      message,
      status: response.status,
      data,
      isNetworkError: false,
    });
  }

  return data;
}

// ── Utilitaire de délai ───────────────────────────────────────────────────────

/**
 * Pause asynchrone.
 *
 * @param {number} ms - Durée en millisecondes
 * @returns {Promise<void>}
 */
function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// ── API publique ──────────────────────────────────────────────────────────────

/**
 * Client HTTP ABBUSINESS.
 *
 * Chaque méthode retourne une Promise<any> (corps JSON) ou lève une ApiError.
 *
 * Exemples :
 *   const products = await api.get('/products');
 *   await api.post('/orders', { product_id: 1, qty: 2 });
 *   await api.delete(`/reviews/${id}`);
 */
const api = {
  /**
   * Requête GET.
   *
   * @param {string} url
   * @param {RequestInit} [options]
   * @returns {Promise<any>}
   */
  get(url, options = {}) {
    return request(url, { ...options, method: 'GET' });
  },

  /**
   * Requête POST.
   *
   * @param {string} url
   * @param {any}    [body]    - Sérialisé en JSON si ce n'est pas un FormData
   * @param {RequestInit} [options]
   * @returns {Promise<any>}
   */
  post(url, body, options = {}) {
    return request(url, {
      ...options,
      method: 'POST',
      body:   body instanceof FormData ? body : JSON.stringify(body),
    });
  },

  /**
   * Requête PUT.
   *
   * @param {string} url
   * @param {any}    [body]
   * @param {RequestInit} [options]
   * @returns {Promise<any>}
   */
  put(url, body, options = {}) {
    return request(url, {
      ...options,
      method: 'PUT',
      body:   body instanceof FormData ? body : JSON.stringify(body),
    });
  },

  /**
   * Requête PATCH.
   *
   * @param {string} url
   * @param {any}    [body]
   * @param {RequestInit} [options]
   * @returns {Promise<any>}
   */
  patch(url, body, options = {}) {
    return request(url, {
      ...options,
      method: 'PATCH',
      body:   body instanceof FormData ? body : JSON.stringify(body),
    });
  },

  /**
   * Requête DELETE.
   *
   * @param {string} url
   * @param {RequestInit} [options]
   * @returns {Promise<any>}
   */
  delete(url, options = {}) {
    return request(url, { ...options, method: 'DELETE' });
  },

  /**
   * Upload de fichier multipart.
   * Utilisé pour l'import CSV et les images produits.
   *
   * @param {string}   url
   * @param {FormData} formData
   * @param {RequestInit} [options]
   * @returns {Promise<any>}
   */
  upload(url, formData, options = {}) {
    return request(url, {
      ...options,
      method: 'POST',
      body:   formData,
      // Ne pas forcer Content-Type : le navigateur le génère avec le boundary
    });
  },
};

export default api;

// ══════════════════════════════════════════════════════════════════════════════
// Exports nommés — compatibilité avec tous les contextes et hooks existants
// ══════════════════════════════════════════════════════════════════════════════

/* Utilitaire interne qs */
const buildQs = (params = {}) =>
  new URLSearchParams(
    Object.fromEntries(
      Object.entries(params)
        .filter(([, v]) => v !== undefined && v !== '' && v !== null)
        .map(([k, v]) => [k, typeof v === 'boolean' ? (v ? '1' : '0') : v])
    )
  ).toString();

/* ── Categories ── */
export const categoriesApi = {
  list:   ()           => request('/categories'),
  get:    (slug)       => request(`/categories/${slug}`),
  create: (data)       => request('/categories',          { method: 'POST',   body: JSON.stringify(data) }),
  update: (slug, data) => request(`/categories/${slug}`,  { method: 'PUT',    body: JSON.stringify(data) }),
  remove: (slug)       => request(`/categories/${slug}`,  { method: 'DELETE' }),
};

/* ── Products ── */
export const productsApi = {
  list: (params = {}) => {
    const qs = buildQs(params);
    return request(`/products${qs ? `?${qs}` : ''}`);
  },
  suggest: (q)         => request(`/products/suggest?q=${encodeURIComponent(q)}`),
  get:    (slug)       => request(`/products/${slug}`),
  create: (data)       => request('/products',            { method: 'POST', body: JSON.stringify(data) }),
  update: (slug, data) => request(`/products/${slug}`,    { method: 'PUT',  body: JSON.stringify(data) }),
  remove: (slug)       => request(`/products/${slug}`,    { method: 'DELETE' }),

  uploadImage: (id, file, isPrimary = false) => {
    const form = new FormData();
    form.append('image',      file);
    form.append('is_primary', isPrimary ? '1' : '0');
    const tok  = getToken();
    const csrf = getCookie('XSRF-TOKEN');
    return fetch(`${API_BASE}/admin/products/${id}/images`, {
      method:      'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...(tok  ? { Authorization:  `Bearer ${tok}` } : {}),
        ...(csrf ? { 'X-XSRF-TOKEN': csrf }             : {}),
      },
      body: form,
    }).then(async r => {
      if (r.status === 401) { window.dispatchEvent(new CustomEvent('auth:unauthorized')); throw new Error('Session expirée.'); }
      const ct   = r.headers.get('content-type') ?? '';
      const json = ct.includes('application/json') ? await r.json() : {};
      if (!r.ok) throw Object.assign(new Error(json.message ?? `Erreur ${r.status}`), { status: r.status, data: json });
      return json;
    });
  },

  deleteImage: (id) => request(`/admin/images/${id}`, { method: 'DELETE' }),

  // Promouvoir une image deja envoyee : is_primary ne se fixait qu'a
  // l'envoi, donc la vignette du catalogue etait figee des la premiere photo.
  setPrimaryImage: (id) => request(`/admin/images/${id}/primary`, { method: 'PATCH' }),

  // Texte alternatif : lu par les lecteurs d'ecran, indexe par les moteurs.
  updateImage: (id, data) =>
    request(`/admin/images/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  // L'ordre doit decrire la galerie entiere, le serveur refuse une liste
  // partielle (deux images se disputeraient alors le meme rang).
  reorderImages: (productId, order) =>
    request(`/admin/products/${productId}/images/reorder`, {
      method: 'POST', body: JSON.stringify({ order }),
    }),
};

/* ── Cart ── */
export const cartApi = {
  get:    ()                        => request('/cart'),
  add:    (productId, qty = 1, extras = {}) =>
    request('/cart/items', { method: 'POST', body: JSON.stringify({ product_id: productId, quantity: qty, ...extras }) }),
  update: (itemId, quantity)        =>
    request(`/cart/items/${itemId}`, { method: 'PATCH', body: JSON.stringify({ quantity }) }),
  remove: (itemId)                  => request(`/cart/items/${itemId}`, { method: 'DELETE' }),
  clear:  ()                        => request('/cart',      { method: 'DELETE' }),
  sync:   (items)                   => request('/cart/sync', { method: 'POST', body: JSON.stringify({ items }) }),
};

/* ── Orders ── */
export const ordersApi = {
  list:           (page = 1)            => request(`/orders?page=${page}`),
  get:            (orderNumber)         => request(`/orders/${orderNumber}`),
  create:         (data)                => request('/orders', { method: 'POST', body: JSON.stringify(data) }),
  cancel:         (orderNumber, body = {}) =>
    request(`/orders/${orderNumber}/cancel`, { method: 'POST', body: JSON.stringify(body) }),
  validateCoupon: (code, amount)        =>
    request('/coupons/validate', { method: 'POST', body: JSON.stringify({ code, amount }) }),
};

/* ── Delivery Zones ── */
export const deliveryApi = {
  zones:     ()               => request('/delivery-zones'),
  calculate: (zone_id, amount) =>
    request(`/delivery-zones/calculate?zone_id=${zone_id}&amount=${amount}`),
};

/* ── Reviews ── */
export const reviewsApi = {
  list:   (slug, page = 1) => request(`/products/${slug}/reviews?page=${page}`),
  create: (slug, data)     => request(`/products/${slug}/reviews`, { method: 'POST', body: JSON.stringify(data) }),
};

/* ── Loyalty ── */
export const loyaltyApi = {
  get:    () => request('/loyalty'),
  redeem: () => request('/loyalty/redeem', { method: 'POST', body: JSON.stringify({}) }),
};

/* ── Wishlist ── */
export const wishlistApi = {
  list:   ()           => request('/wishlist'),
  add:    (product_id) => request('/wishlist',             { method: 'POST',   body: JSON.stringify({ product_id }) }),
  remove: (productId)  => request(`/wishlist/${productId}`, { method: 'DELETE' }),
  check:  (productId)  => request(`/wishlist/check/${productId}`),
};

/* ── Profile ── */
export const profileApi = {
  update:         (data) => request('/auth/profile', { method: 'PATCH', body: JSON.stringify(data) }),
  updatePassword: (data) => request('/auth/password', { method: 'PATCH', body: JSON.stringify(data) }),
};

/* ── Notifications ── */
export const notificationsApi = {
  list:        ()   => request('/notifications'),
  markRead:    (id) => request(`/notifications/${id}/read`, { method: 'PATCH' }),
  markAllRead: ()   => request('/notifications/read-all',   { method: 'POST' }),
  remove:      (id) => request(`/notifications/${id}`,      { method: 'DELETE' }),
};

/* ── Tracking ── */
export const trackingApi = {
  get: (orderNumber, phone) =>
    request(`/track/${encodeURIComponent(orderNumber)}?phone=${encodeURIComponent(phone ?? '')}`),
};

/* ── Adresses ── */
export const addressApi = {
  list:       ()         => request('/addresses'),
  create:     (data)     => request('/addresses',               { method: 'POST',  body: JSON.stringify(data) }),
  update:     (id, data) => request(`/addresses/${id}`,         { method: 'PUT',   body: JSON.stringify(data) }),
  remove:     (id)       => request(`/addresses/${id}`,         { method: 'DELETE' }),
  setDefault: (id)       => request(`/addresses/${id}/default`, { method: 'PATCH' }),
};

/* ── Parrainage ── */
export const referralApi = {
  get:   ()     => request('/referral'),
  apply: (code) => request('/referral/apply', { method: 'POST', body: JSON.stringify({ code }) }),
};

/* ── Auth complet (2FA, Google OAuth, email) ── */
/*
 | Commande sans compte.
 |
 | Le panier d'un visiteur vit en localStorage : il n'existe pas cote
 | serveur. On envoie donc les lignes, et le serveur relit les prix en base
 | — ce qui part d'ici n'est qu'une intention d'achat.
 */
export const guestOrdersApi = {
  create: (payload) =>
    request('/guest/orders', { method: 'POST', body: JSON.stringify(payload) }),
};

export const authApi = {
  login:              (email, password) =>
    request('/auth/login',        { method: 'POST', body: JSON.stringify({
      email, password, device_id: getDeviceId(), device_token: getDeviceToken(),
    }) }),
  /*
   | Le defi remplace l'identifiant numerique. Il n'est emis par /auth/login
   | qu'apres verification du mot de passe : le transmettre prouve que le
   | premier facteur a ete franchi. L'ancien user_id ne prouvait rien et
   | s'inventait.
   */
  verify2fa:          async (challenge, code) => {
    const res = await request('/auth/verify-2fa', { method: 'POST', body: JSON.stringify({
      challenge, code, device_id: getDeviceId(),
    }) });

    // Le serveur n'en remet pas aux roles d'administration : ils repassent
    // par le code a chaque connexion.
    if (res.device_token) setDeviceToken(res.device_token);

    return res;
  },
  /*
   | Le serveur oublie les navigateurs memorises ; on efface le notre dans
   | la foulee. Le garder ne servirait a rien — il serait refuse — mais
   | laisserait croire que ce navigateur est encore reconnu.
   */
  logoutAll:          async () => {
    const res = await request('/auth/logout-all', { method: 'POST' });
    clearDeviceToken();
    return res;
  },
  resend2fa:          (challenge)       =>
    request('/auth/resend-2fa',   { method: 'POST', body: JSON.stringify({ challenge }) }),
  resendVerification: (email)           =>
    request('/auth/resend-verification', { method: 'POST', body: JSON.stringify({ email }) }),
  googleFinalize:     (pending_key, code, password) =>
    request('/auth/google/finalize',    { method: 'POST', body: JSON.stringify({ pending_key, code, password }) }),
  googleResendSetup:  (pending_key)     =>
    request('/auth/google/resend-setup', { method: 'POST', body: JSON.stringify({ pending_key }) }),
  me: () => request('/auth/me'),
};

export const authVerifyApi = {
  resend: (email) => request('/auth/resend-verification', { method: 'POST', body: JSON.stringify({ email }) }),
};

/* ── Admin ── */
export const adminApi = {
  // Dashboard & reporting
  sseTicket:  ()                    => request('/admin/sse/ticket', { method: 'POST' }),
  dashboard:  ()                    => request('/admin/dashboard'),
  analytics:  (period = 30)         => request(`/admin/analytics?period=${period}`),
  topClients: (period = 0, limit = 10) =>
    request(`/admin/reporting/top-clients?period=${period}&limit=${limit}`),

  // Audit
  auditLogs:     (p = {}) => { const q = buildQs(p); return request(`/admin/audit-logs${q ? `?${q}` : ''}`); },
  auditForModel: (type, id) => request(`/admin/audit-logs/${type}/${id}`),

  // Products
  listProducts:       (p = {})     => { const q = buildQs(p); return request(`/admin/products${q ? `?${q}` : ''}`); },
  createProduct:      (d)          => request('/admin/products',           { method: 'POST', body: JSON.stringify(d) }),
  updateProduct:      (slug, d)    => request(`/admin/products/${slug}`,   { method: 'PUT',  body: JSON.stringify(d) }),
  deleteProduct:      (slug)       => request(`/admin/products/${slug}`,   { method: 'DELETE' }),
  uploadProductImage: (id, f, p)   => productsApi.uploadImage(id, f, p),
  deleteProductImage: (id)         => productsApi.deleteImage(id),
  setPrimaryProductImage: (id)     => productsApi.setPrimaryImage(id),
  updateProductImage: (id, d)      => productsApi.updateImage(id, d),
  reorderProductImages: (pid, o)   => productsApi.reorderImages(pid, o),

  // Bulk product actions
  bulkDeleteProducts: (ids) =>
    request('/admin/produits/bulk-delete', { method: 'POST', body: JSON.stringify({ ids }) }),
  bulkStatusProducts: (ids, is_active) =>
    request('/admin/produits/bulk-status', { method: 'POST', body: JSON.stringify({ ids, is_active }) }),

  // Variants
  listVariants: (productId) => request(`/admin/products/${productId}/variants`),
  createVariant: (productId, data) =>
    request(`/admin/products/${productId}/variants`, { method: 'POST', body: JSON.stringify(data) }),
  updateVariant: (id, data) =>
    request(`/admin/variants/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteVariant: (id)       => request(`/admin/variants/${id}`, { method: 'DELETE' }),

  // Categories
  listCategories:  ()          => request('/admin/categories'),
  createCategory:  (d)         => request('/admin/categories',         { method: 'POST', body: JSON.stringify(d) }),
  updateCategory:  (slug, d)   => request(`/admin/categories/${slug}`, { method: 'PUT',  body: JSON.stringify(d) }),
  deleteCategory:  (slug)      => request(`/admin/categories/${slug}`, { method: 'DELETE' }),

  // Orders
  listOrders:        (p = {}) => { const q = buildQs(p); return request(`/admin/orders${q ? `?${q}` : ''}`); },
  exportOrders: (params = {}) => {
    const qs   = buildQs(params);
    const tok  = getToken();
    const csrf = getCookie('XSRF-TOKEN');
    return fetch(`${API_BASE}/admin/orders/export${qs ? `?${qs}` : ''}`, {
      credentials: 'include',
      headers: {
        Accept: 'text/csv',
        ...(tok  ? { Authorization:  `Bearer ${tok}` } : {}),
        ...(csrf ? { 'X-XSRF-TOKEN': csrf }             : {}),
      },
    });
  },
  getOrder:           (id)          => request(`/admin/orders/${id}`),
  updateOrderStatus:  (id, status)  =>
    request(`/admin/orders/${id}/status`,   { method: 'PATCH', body: JSON.stringify({ status }) }),
  updateOrderNotes:   (id, notes)   =>
    request(`/admin/orders/${id}/notes`,    { method: 'PATCH', body: JSON.stringify({ admin_notes: notes }) }),
  updateOrderTracking:(id, data)    =>
    request(`/admin/orders/${id}/tracking`, { method: 'PATCH', body: JSON.stringify(data) }),
  bulkStatus: (ids, status)         =>
    request('/admin/orders/bulk-status',    { method: 'POST',  body: JSON.stringify({ ids, status }) }),
  listLivreurs:       ()                    => request('/admin/livreurs'),
  listPreparateurs:   ()                    => request('/admin/preparateurs'),
  confirmOrder:       (id)                  => request(`/admin/orders/${id}/confirm`, { method: 'POST' }),
  rejectOrder:        (id, reason)          => request(`/admin/orders/${id}/reject`,  { method: 'POST', body: JSON.stringify({ reason }) }),
  assignPreparateur:  (id, preparateurId)   => request(`/admin/orders/${id}/assign-preparateur`, { method: 'POST', body: JSON.stringify({ preparateur_id: preparateurId }) }),
  assignLivreur: (orderId, livreurId) =>
    request(`/admin/orders/${orderId}/assign`, { method: 'POST', body: JSON.stringify({ livreur_id: livreurId }) }),
  generateQr:    (orderId)      =>
    request(`/admin/orders/${orderId}/qr`, { method: 'POST' }),

  // Users
  listUsers:      (p = {}) => { const q = buildQs(p); return request(`/admin/users${q ? `?${q}` : ''}`); },
  updateUserRole:     (id, role) =>
    request(`/admin/users/${id}/role`, { method: 'PATCH', body: JSON.stringify({ role }) }),
  getCustomer:        (id)       => request(`/admin/users/${id}`),
  updateCustomerNote: (id, note) =>
    request(`/admin/users/${id}/note`, { method: 'PATCH', body: JSON.stringify({ note }) }),

  // Reviews
  listReviews:   (p = {}) => { const q = buildQs(p); return request(`/admin/reviews${q ? `?${q}` : ''}`); },
  approveReview: (id)      => request(`/admin/reviews/${id}/approve`, { method: 'PATCH' }),
  rejectReview:  (id)      => request(`/admin/reviews/${id}/reject`,  { method: 'PATCH' }),
  deleteReview:  (id)      => request(`/admin/reviews/${id}`,         { method: 'DELETE' }),

  // Delivery Zones
  listDeliveryZones:  ()       => request('/admin/delivery-zones'),
  createDeliveryZone: (d)      => request('/admin/delivery-zones',      { method: 'POST', body: JSON.stringify(d) }),
  updateDeliveryZone: (id, d)  => request(`/admin/delivery-zones/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteDeliveryZone: (id)     => request(`/admin/delivery-zones/${id}`, { method: 'DELETE' }),

  // Coupons
  listCoupons:   (p = {}) => { const q = buildQs(p); return request(`/admin/coupons${q ? `?${q}` : ''}`); },
  createCoupon:  (d)       => request('/admin/coupons',      { method: 'POST', body: JSON.stringify(d) }),
  updateCoupon:  (id, d)   => request(`/admin/coupons/${id}`, { method: 'PUT',  body: JSON.stringify(d) }),
  deleteCoupon:  (id)      => request(`/admin/coupons/${id}`, { method: 'DELETE' }),

  // Import CSV
  importCatalogPreview: (fd) =>
    request('/admin/import/catalog/preview', { method: 'POST', body: fd }),
  importCatalog:        (fd) =>
    request('/admin/import/catalog',         { method: 'POST', body: fd }),

  // Fidélité
  loyaltyStats:   ()       => request('/admin/loyalty'),
  loyaltyClients: (p = {}) => { const q = buildQs(p); return request(`/admin/loyalty/clients${q ? `?${q}` : ''}`); },
  loyaltyAdjust:  (d)      => request('/admin/loyalty/adjust', { method: 'POST', body: JSON.stringify(d) }),
};

/* ── Staff management ── */
export const staffManagementApi = {
  listStaff:       ()             => request('/admin/staff'),
  searchClients:   (search)       =>
    request(`/admin/staff/search-clients?search=${encodeURIComponent(search)}`),
  promoteUser:     (userId, role) =>
    request('/admin/staff', { method: 'POST', body: JSON.stringify({ user_id: userId, role }) }),
  updateStaffRole: (id, role)     =>
    request(`/admin/staff/${id}/role`, { method: 'PATCH', body: JSON.stringify({ role }) }),
  revokeStaff:     (id)           => request(`/admin/staff/${id}`, { method: 'DELETE' }),
};

/* ── Préparateur ── */
export const staffApi = {
  listOrders:   (p = {})      => { const q = buildQs(p); return request(`/staff/orders${q ? `?${q}` : ''}`); },
  updateStatus: (id, status)  =>
    request(`/staff/orders/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),
};

/* ── Livreur ── */
export const driverApi = {
  listOrders:   (p = {})     => { const q = buildQs(p); return request(`/driver/orders${q ? `?${q}` : ''}`); },
  markContact:  (id)         => request(`/driver/orders/${id}/contact`, { method: 'POST' }),
  updateStatus: (id, status) =>
    request(`/driver/orders/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),
  markPaid:     (id)         => request(`/driver/orders/${id}/payment`, { method: 'POST' }),
  generateQr:   (id)         => request(`/driver/orders/${id}/qr`,      { method: 'POST' }),
};
